import { __ } from '@wordpress/i18n';
import { compose } from '@wordpress/compose';
import { withSelect, withDispatch, useSelect } from '@wordpress/data';
import { PluginPostStatusInfo } from '@wordpress/editor';

import { Button, Spinner, BaseControl, TextControl, __experimentalVStack as VStack } from '@wordpress/components';
import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';

const SettingsPanel = ( { postType, postMeta, setPostMeta } ) => {
	if ( postType !== 'gwx_testimonial' ) return null;

    // Expect meta.gwx_photo to be a number (attachment ID)
    const photoId = Number( postMeta?.gwx_photo || 0 );
    const author  = postMeta?.gwx_author || '';
    const citation  = postMeta?.gwx_citation || '';
    const jobTitle  = postMeta?.gwx_job_title || '';

    const setPhotoId = ( value ) => {
        setPostMeta( { ...postMeta, gwx_photo: value || 0 } ); // store 0 if cleared
    };

    const setAuthor = ( value ) => {
        setPostMeta( { ...postMeta, gwx_author: value } );
    };

    const setCitation = ( value ) => {
        setPostMeta( { ...postMeta, gwx_citation: value } );
    };

    const setJobTitle = ( value ) => {
        setPostMeta( { ...postMeta, gwx_job_title: value } );
    };

    // Fetch media for preview if we have an ID
    const media = useSelect(
        ( select ) => ( photoId ? select( 'core' ).getMedia( photoId ) : null ),
        [ photoId ]
    );

    const url   = media?.source_url;

    return (
        <PluginPostStatusInfo>
            <VStack spacing="1rem" style={{ width: '100%' }}>
               
                <TextControl
                    label={ __( 'Author', 'groundworx-testimonial' ) }
                    value={ author }
                    onChange={ setAuthor }
                    placeholder={ __( 'Enter Author…', 'groundworx-testimonial' ) }
                />
                
                <TextControl
                    label={ __( 'Business Name', 'groundworx-testimonial' ) }
                    value={ citation }
                    onChange={ setCitation }
                    placeholder={ __( 'Enter Business Name…', 'groundworx-testimonial' ) }
                />

                <TextControl
                    label={ __( 'Job Title', 'groundworx-testimonial' ) }
                    value={ jobTitle }
                    onChange={ setJobTitle }
                    placeholder={ __( 'Enter Job Title…', 'groundworx-testimonial' ) }
                />

                <BaseControl
                    label={ __( 'Photo', 'groundworx-testimonial' ) }
                    id="gwx-photo-control"
                >
                    <MediaUploadCheck>
                        <MediaUpload
                            onSelect={ ( m ) => setPhotoId( m?.id || 0 ) }
                            allowedTypes={ [ 'image' ] }
                            value={ photoId || undefined }
                            render={ ( { open } ) => (
                                <>
                                    { photoId ? (
                                        <div style={{ display: 'grid', gap: 8 }}>
                                            { media ? (
                                                <img
                                                    src={ url }
                                                    alt={ media?.alt_text || '' }
                                                    style={{
                                                        maxWidth: '100%',
                                                        height: 'auto',
                                                        maxHeight: 120,
                                                        objectFit: 'contain',
                                                        background: '#fff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: 6,
                                                        padding: 8
                                                    }}
                                                />
                                            ) : (
                                                <Spinner />
                                            ) }

                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Button variant="secondary" onClick={ open }>
                                                    { __( 'Replace', 'groundworx-testimonial' ) }
                                                </Button>
                                                <Button variant="link" onClick={ () => setPhotoId( 0 ) }>
                                                    { __( 'Remove', 'groundworx-testimonial' ) }
                                                </Button>
                                            </div>
                                        
                                        </div>
                                    ) : (
                                        <Button variant="primary" onClick={ open }>
                                            { __( 'Select Photo', 'groundworx-testimonial' ) }
                                        </Button>
                                    ) }
                                </>
                            ) }
                        />
                    </MediaUploadCheck>
                </BaseControl>
            </VStack>
        </PluginPostStatusInfo>
    );
};

export default compose( [
	withSelect( ( select ) => {
		const editor = select( 'core/editor' );
		return {
            isSavingPost: editor.isSavingPost(),
            postId      : editor.getCurrentPostId(),
            postMeta    : editor.getEditedPostAttribute( 'meta' ),
            postType    : editor.getCurrentPostType(),
		};
	} ),
	withDispatch( ( dispatch ) => {
		return {
			setPostMeta( newMeta ) {
				dispatch( 'core/editor' ).editPost( { meta: newMeta } );
			},
		};
	} ),
] )( SettingsPanel );